import { useState } from "react";
import { SettingOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Dropdown, Menu } from "antd";
import { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { productClientActions, productOrdersActions } from "@fitness-app/app-store";
import {
  canCloseOrderAsCanceled,
  canCloseOrderAsFailed,
  canConfirmManualOrder,
  hasBlikChargeInFlight,
  OrderStatusOption,
  type ClientInvoiceData,
  type Order,
} from "@fitness-app/data-models/entities/Order";
import { PaymentClient } from "@fitness-app/data-models/entities/Product";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useEntityChange } from "~/hooks/useEntityChange";
import InvoiceFormData, { type ExtendedModel } from "~/modules/Products/components/InvoiceFormData/InvoiceFormData";
import { useAppDispatch } from "~/store/initializeStore";
import { useConfirmManualPayment, type ConfirmPaymentDocumentIssuance } from "../../hooks/useConfirmManualPayment";
import ConfirmPaymentModal from "./ConfirmPaymentModal";
import { useCancelOrder } from "../../hooks/useCancelOrder";
import { useMarkManualPaymentAsFailed } from "../../hooks/useMarkManualPaymentAsFailed";

interface IssueInvoiceProps {
  order: Order;
  selectedMonth?: Dayjs;
  ownerId?: string;
  onInvoiceIssueSuccess?: () => void;
}

const IssueInvoice = ({ order, selectedMonth, ownerId, onInvoiceIssueSuccess }: IssueInvoiceProps) => {
  const { t } = useTranslation(["products", "client", "common"]);
  const [showInvoiceForm, toggleInvoiceForm] = useState<false | "issueReceipt" | "issueInvoice">(false);
  const [loader, onSuccess, onError, onStart] = useEntityChange(() => {
    toggleInvoiceForm(false);
  });
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null | { message: string; details: string }>(null);
  const { mutate: markManualPaymentAsFailed } = useMarkManualPaymentAsFailed(selectedMonth, ownerId ?? "");
  const { mutate: cancelOrder } = useCancelOrder(selectedMonth, ownerId ?? "");
  const { mutate: markManualPaymentAsPaid, isLoading: confirmPaymentLoading } = useConfirmManualPayment(
    selectedMonth,
    ownerId ?? "",
  );
  const [confirmPaymentOpen, toggleConfirmPayment] = useState(false);
  const queryClient = useQueryClient();

  const confirmPayment = (documentIssuance?: ConfirmPaymentDocumentIssuance) => {
    markManualPaymentAsPaid(
      {
        productId: order.product,
        orderId: order.id,
        clientEmail: order.clientEmail,
        documentIssuance,
      },
      {
        onSuccess: () => {
          toggleConfirmPayment(false);
          // Lista zamówień rodzica żyje w reduxie — bez tego wiersz zostaje
          // "nieopłacony" do ręcznego odświeżenia strony.
          onInvoiceIssueSuccess?.();
        },
      },
    );
  };

  // Nieopłacona rata z fakturą Stripe'a — kartą albo BLIK-iem przez TPay — da się
  // domknąć przelewem tak samo: backend opłaca fakturę `paid_out_of_band`, a
  // kolejka ponowień BLIK pomija opłacone zamówienia. Wyjątek to BLIK z
  // obciążeniem jeszcze w toku — TPay może je rozliczyć po naszym potwierdzeniu.
  const canConfirmPayment =
    (order.paymentGateway === PaymentClient.MANUAL && canConfirmManualOrder(order.status)) ||
    Boolean(
      order.paymentGatewayInvoiceId &&
      order.status === OrderStatusOption.Failed &&
      (order.paymentGateway === PaymentClient.STRIPE ||
        (order.paymentGateway === PaymentClient.TPAY && !hasBlikChargeInFlight(order.metadata))),
    );

  /**
   * Dwa sposoby domknięcia nieopłaconego zamówienia (TRE-1999). Sprzątają tak
   * samo — faktura schodzi z otwartych, subskrypcja leci — różnią się tym, co
   * zostaje w bazie: „nieopłacone" liczy się do statystyki odmów płatności,
   * „anulowane" nie. Widoczność każdej pozycji pilnują predykaty z data-models,
   * trzymane w parze z `OrderStatusMachine` — stąd asymetria: anulowanego
   * zamówienia nie da się już oznaczyć jako nieopłacone.
   */
  const closeOrderItems = [
    ...(canCloseOrderAsFailed(order.status)
      ? [
          {
            key: "markAsFailed",
            danger: true,
            label: t("client:invoice.markAsFailed"),
            onClick: () => {
              markManualPaymentAsFailed(
                { productId: order.product, orderId: order.id },
                { onSuccess: () => onInvoiceIssueSuccess?.() },
              );
            },
          },
        ]
      : []),
    ...(canCloseOrderAsCanceled(order.status)
      ? [
          {
            key: "markAsCanceled",
            danger: true,
            label: t("client:invoice.markAsCanceled"),
            onClick: () => {
              cancelOrder(
                { productId: order.product, orderId: order.id },
                { onSuccess: () => onInvoiceIssueSuccess?.() },
              );
            },
          },
        ]
      : []),
  ];

  const updateInvoiceData = async (data: ClientInvoiceData | ExtendedModel) => {
    onStart();
    setErrorMessage(null);
    try {
      if ("updateClientData" in data) {
        const { sellDate, issueDate, updateClientData, ...rest } = data;
        await dispatch(
          productClientActions.updateInvoiceData({
            payload: {
              invoiceData: rest,
              clientEmail: order.clientEmail,
              updateClientData,
              sellDate,
              issueDate,
              orderId: order.id,
            },
          }),
        );
      }

      await dispatch(
        productClientActions.issueInvoice({
          ownerId: order.owner ?? "",
          productId: order.product,
          orderId: order.id,
          updateClientData: "updateClientData" in data ? data.updateClientData : false,
          sendInvoiceToClient: "sendInvoiceToClient" in data ? data.sendInvoiceToClient : false,
          issueReceipt: showInvoiceForm === "issueReceipt",
        }),
      ).unwrap();

      onSuccess();
      void queryClient.invalidateQueries(["productsTransactions"]);
      if (selectedMonth) {
        void dispatch(productOrdersActions.fetchOrders({ productId: order.product, selectedMonth, ownerId }));
      }
      onInvoiceIssueSuccess?.();
      toggleInvoiceForm(false);
    } catch (e) {
      if (e instanceof Error) {
        if ("code" in e && e.code === "invalid-argument") {
          setErrorMessage(JSON.parse(e.message) as { message: string; details: string });
        } else {
          setErrorMessage(e.message);
        }
      }

      onError();
    }
  };

  if (order.orderForImport || (order.connectedInvoice && "number" in order.connectedInvoice)) {
    if (order.paymentGateway === PaymentClient.MANUAL && canConfirmManualOrder(order.status)) {
      return (
        <div className="ml-2 flex-1 text-right">
          <Dropdown
            overlay={
              <Menu
                items={[
                  {
                    key: "markAsPaid",
                    label: t("client:invoice.markAsPaid"),
                    onClick: () => toggleConfirmPayment(true),
                  },
                  ...closeOrderItems,
                ]}
              />
            }
          >
            <SettingOutlined />
          </Dropdown>
          <ConfirmPaymentModal
            open={confirmPaymentOpen}
            loading={confirmPaymentLoading}
            onCancel={() => toggleConfirmPayment(false)}
            onConfirm={confirmPayment}
          />
          <ModalForm<ExtendedModel>
            open={Boolean(showInvoiceForm)}
            onCancel={() => toggleInvoiceForm(false)}
            title={
              showInvoiceForm === "issueReceipt"
                ? t("client:invoice.issueReceiptTitle")
                : t("client:invoice.issueInvoiceTitle")
            }
            okText={
              showInvoiceForm === "issueReceipt" ? t("client:invoice.issueReceipt") : t("client:invoice.issueInvoice")
            }
            loading={!!loader}
          >
            <InvoiceFormData onSubmit={updateInvoiceData} model={order.client} withInvoiceMetadata order={order}>
              {errorMessage && (
                <Alert
                  showIcon
                  style={{ marginBottom: 20 }}
                  type="error"
                  message={t("client:invoice.errorTitle")}
                  description={
                    typeof errorMessage === "string" ? (
                      errorMessage
                    ) : (
                      <div>
                        <div>{errorMessage.message}</div>
                        <div>
                          {errorMessage.details
                            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                              (JSON.parse(errorMessage.details)?.message as string) || "Unknown error"
                            : "Unknown error"}
                        </div>
                      </div>
                    )
                  }
                />
              )}
            </InvoiceFormData>
          </ModalForm>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="ml-2 flex-1 text-right">
      <Dropdown
        overlay={
          <Menu
            items={[
              // „Potwierdź płatność" ma własną bramkę — dla Stripe'a tylko
              // `Failed`, bo `Canceled` znaczy, że fakturę zdjęliśmy z otwartych
              // i nie ma czego potwierdzać. Ścieżkę „zapłacone przelewem po
              // anulowaniu" łapie `canConfirmManualOrder` dla płatności ręcznych.
              ...(canConfirmPayment
                ? [
                    {
                      key: "markAsPaid",
                      label: t("client:invoice.markAsPaid"),
                      onClick: () => toggleConfirmPayment(true),
                    },
                  ]
                : []),
              // Domknięcie zamówienia ma bramkę osobną: da się je zamknąć także
              // wtedy, gdy nie ma czego potwierdzać (Stripe w CREATED albo
              // SCHEDULED) — wcześniej obie akcje wisiały pod jednym warunkiem
              // i anulowanie było tam nieosiągalne.
              ...closeOrderItems,
              {
                key: "2",
                label:
                  order.connectedInvoice && "errorMessage" in order.connectedInvoice
                    ? t("client:invoice.retryReceiptIssue")
                    : t("client:invoice.issueReceipt"),
                onClick: () => toggleInvoiceForm("issueReceipt"),
              },
              {
                key: "1",
                label:
                  order.connectedInvoice && "errorMessage" in order.connectedInvoice
                    ? t("client:invoice.retryInvoiceIssue")
                    : t("client:invoice.issueInvoice"),
                onClick: () => toggleInvoiceForm("issueInvoice"),
              },
            ]}
          />
        }
      >
        <SettingOutlined />
      </Dropdown>
      <ConfirmPaymentModal
        open={confirmPaymentOpen}
        loading={confirmPaymentLoading}
        onCancel={() => toggleConfirmPayment(false)}
        onConfirm={confirmPayment}
      />
      <ModalForm<ExtendedModel>
        open={Boolean(showInvoiceForm)}
        onCancel={() => toggleInvoiceForm(false)}
        title={
          showInvoiceForm === "issueReceipt"
            ? t("client:invoice.issueReceiptTitle")
            : t("client:invoice.issueInvoiceTitle")
        }
        okText={
          showInvoiceForm === "issueReceipt" ? t("client:invoice.issueReceipt") : t("client:invoice.issueInvoice")
        }
        loading={!!loader}
      >
        <InvoiceFormData onSubmit={updateInvoiceData} model={order.client} withInvoiceMetadata order={order}>
          {errorMessage && (
            <Alert
              showIcon
              style={{ marginBottom: 20 }}
              type="error"
              message={t("client:invoice.errorTitle")}
              description={
                typeof errorMessage === "string" ? (
                  errorMessage
                ) : (
                  <div>
                    <div>{errorMessage.message}</div>
                    <div>
                      {errorMessage.details
                        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          (JSON.parse(errorMessage.details)?.message as string) || "Unknown error"
                        : "Unknown error"}
                    </div>
                  </div>
                )
              }
            />
          )}
        </InvoiceFormData>
      </ModalForm>
    </div>
  );
};

export default IssueInvoice;
